import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Paperclip, Download, Loader2, FileText, Upload } from 'lucide-react';
import { GlassCard } from '@/components/custom/GlassCard';
import { apiFetchBlob } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { fetchTicketAttachments, uploadTicketAttachment } from '@/hooks/useAddressValidation';
import type { PublicAttachmentItem } from '@uniticket/shared';

// Files/images attached to the ticket on Uniticket, surfaced in the validation window so the reviewer has
// the full picture. Fetched on-demand (an extra Uniticket call via the backend proxy). Image previews +
// downloads go through apiFetchBlob because an <img>/<a> can't carry the JWT header.
// The card also UPLOADS (user ask 2026-08-06): the verdict panel's picker only sends a file along with an
// action (Save / hand-over / Send to Build), so a ticket already parked in « En attente de réponse » had no
// way to receive one — the two buttons that could send it would have re-posted a note. That was the
// UT-2026-000506 dead end (Juliana holding a note that points at a file nobody could attach). Here the file
// goes to the ticket on its own: no comment, no status change, no verdict.

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // Uniticket's cap (public API §7.6) — same as the verdict panel.

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function TicketAttachments({ ticketId }: { ticketId: string }) {
  const { t } = useTranslation(['tickets']);
  const [items, setItems] = useState<PublicAttachmentItem[] | null>(null);
  // false = the Uniticket attachment channel answered with an error. Reading failed, so writing would too:
  // the uploader is hidden rather than offering a button that can only fail.
  const [channelOk, setChannelOk] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchTicketAttachments(ticketId)
      .then((res) => { if (alive) { setItems(res.data); setChannelOk(true); } })
      .catch(() => { if (alive) { setItems([]); setChannelOk(false); } }); // never block the page
    return () => { alive = false; };
  }, [ticketId]);

  async function upload(file: File) {
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(t('tickets:validation.attachments.tooBig'));
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      await uploadTicketAttachment(ticketId, file);
      // Re-read rather than push the response: the tile list then shows exactly what Uniticket stored
      // (its own id, the name it kept, the size it accepted).
      setItems((await fetchTicketAttachments(ticketId)).data);
      toast.success(t('tickets:validation.attachments.uploaded'));
    } catch (err) {
      // Same rule as everywhere else in this window: the reviewer must SEE why, not guess from silence.
      const message = (err as Error)?.message || String(err);
      setUploadError(message);
      toast.error(t('tickets:validation.attachments.uploadFailed', { error: message }));
    } finally {
      setUploading(false);
    }
  }

  // Always render the section (even with none) so the reviewer knows WHERE attachments appear — the operator
  // often references a file in the comment, and a hidden card read as "the app has no attachments feature".
  return (
    <GlassCard className="p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Paperclip className="h-4 w-4 text-brand-rose" />
        <h2 className="text-sm font-bold text-slate-800">{t('tickets:validation.attachments.title')}</h2>
        {items && items.length > 0 && (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
            {items.length}
          </span>
        )}
        {channelOk && (
          <label
            title={t('tickets:validation.attachments.uploadHint')}
            className={cn(
              'ml-auto inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-300',
              uploading && 'pointer-events-none opacity-50',
            )}
          >
            {uploading
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              : <Upload className="h-3.5 w-3.5" aria-hidden="true" />}
            {uploading
              ? t('tickets:validation.attachments.uploading')
              : t('tickets:validation.attachments.uploadBtn')}
            <input
              type="file"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                e.target.value = ''; // so the same file can be re-picked after a failure
                if (f) void upload(f);
              }}
            />
          </label>
        )}
      </div>
      {channelOk && (
        <p className="-mt-1 mb-3 text-[11px] text-slate-400">
          {t('tickets:validation.attachments.uploadHint')}
        </p>
      )}
      {uploadError && (
        <p className="mb-3 text-[11px] font-medium text-red-500" title={uploadError}>
          {t('tickets:validation.attachments.uploadFailed', { error: uploadError })}
        </p>
      )}
      {items === null ? (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('tickets:validation.attachments.loading')}
        </div>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-400">{t('tickets:validation.attachments.empty')}</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((a) => (
            <AttachmentTile key={a.id} ticketId={ticketId} item={a} />
          ))}
        </div>
      )}
    </GlassCard>
  );
}

// Mime types the browser can display in a tab — clicking those tiles OPENS the file; anything else downloads.
const VIEWABLE_MIME = /^(image\/|text\/|application\/pdf)/;

// blob: viewers don't reliably honor the charset parameter of the blob's type (Chrome renders text as
// Latin-1 regardless) — for UTF-8 text, prepend a BOM so the tab decodes it right. Text the server flagged
// windows-1252 is left untouched (a BOM would be a lie there). Downloads keep the original bytes.
async function viewerBlob(blob: Blob, type: string): Promise<Blob> {
  if (!/^text\//i.test(type) || /windows-1252/i.test(type)) return blob;
  const text = await blob.text();
  const bom = String.fromCharCode(0xfeff);
  return new Blob([text.charCodeAt(0) === 0xfeff ? text : bom + text], { type });
}

function AttachmentTile({ ticketId, item }: { ticketId: string; item: PublicAttachmentItem }) {
  const { t } = useTranslation(['tickets']);
  const isImage = item.mime_type.startsWith('image/');
  const dlPath = `/tickets/${ticketId}/attachments/${item.id}/download`;
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Image previews are fetched as authenticated blobs → object URLs (revoked on unmount).
  useEffect(() => {
    if (!isImage) return;
    let alive = true;
    let url: string | null = null;
    apiFetchBlob(dlPath)
      .then((blob) => { if (!alive) return; url = URL.createObjectURL(blob); setImgUrl(url); })
      .catch(() => { if (alive) setPreviewFailed(true); }); // fall back to the file icon; open/download will surface WHY
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [dlPath, isImage]);

  function saveBlob(blob: Blob) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = item.file_name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // Click-to-open on the WHOLE tile, whatever the type (v1 only made image previews clickable — .txt/.pdf
  // tiles looked dead). Viewable types open in a new tab; the rest — or a blocked popup — download instead.
  async function open() {
    setBusy(true);
    setError(null);
    try {
      const blob = await apiFetchBlob(dlPath);
      const type = blob.type || item.mime_type;
      if (VIEWABLE_MIME.test(type)) {
        const url = URL.createObjectURL(await viewerBlob(blob, type));
        const win = window.open(url, '_blank');
        if (win) win.opener = null;
        else saveBlob(blob);
        // The viewer tab loads the URL asynchronously — revoke later, not on the spot.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        saveBlob(blob);
      }
    } catch (e) {
      setError((e as Error).message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function download() {
    setBusy(true);
    setError(null);
    try {
      saveBlob(await apiFetchBlob(dlPath));
    } catch (e) {
      setError((e as Error).message || String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-slate-200">
      {isImage && imgUrl ? (
        <a href={imgUrl} target="_blank" rel="noreferrer" className="block" title={t('tickets:validation.attachments.open')}>
          <img
            src={imgUrl}
            alt={item.file_name}
            className="h-32 w-full bg-slate-50 object-cover"
            onError={() => { setImgUrl(null); setPreviewFailed(true); }}
          />
        </a>
      ) : (
        <button
          type="button"
          onClick={open}
          disabled={busy}
          title={t('tickets:validation.attachments.open')}
          aria-label={`${t('tickets:validation.attachments.open')} — ${item.file_name}`}
          className="flex h-32 w-full items-center justify-center bg-slate-50 hover:bg-slate-100 disabled:opacity-50"
        >
          {busy || (isImage && !previewFailed) ? (
            <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
          ) : (
            <FileText className="h-10 w-10 text-slate-300" />
          )}
        </button>
      )}
      <div className="flex items-center gap-2 p-2.5">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-slate-700" title={item.file_name}>{item.file_name}</p>
          <p className="text-[10px] text-slate-400">{humanSize(item.file_size)}</p>
          {error && (
            <p className="mt-0.5 text-[10px] font-medium text-red-500" title={error}>
              {t('tickets:validation.attachments.openFailed', { error })}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={download}
          disabled={busy}
          title={t('tickets:validation.attachments.download')}
          aria-label={t('tickets:validation.attachments.download')}
          className="shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-brand-rose disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
